import React from 'react';
import {Box, Typography} from '@mui/material';
import {FieldInputProps, FieldMetaProps} from 'formik';
import {Pot} from '../../../schema-types/Pot';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {contributeFieldType} from './contributeFields';
import type {newPlanFields} from '../RecommendsSetUpNew/setUpNewFields';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';
import {hasWrapper} from '../CreateRecommend/RecommendSourceDestination/utils';
import {ContributeNotSubplanDestinationAmounts} from '../CreateRecommend/RecommendSourceDestination/ContributeNotSubplanDestinationAmounts';
import {
  getSummary,
  noSubplansMessage,
} from '../CreateRecommend/RecommendSourceDestination/naming';
import {getValidation} from '../CreateRecommend/RecommendSourceDestination/validators';
import {NewPlan} from '../RecommendsSetUpNew/NewPlan';
import {ContributeExistingSubplans} from './ContributeExistingSubplans';
import {getSavedChildSubPlans} from '../RecommendsSetUpNew/ListNewSubPlans';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {includesSaltus} from '../../../utils/includesSaltus';
import {getDisableChangingPlan} from './ContributeHelpers';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import RecommendPlanForm from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';

const PostContributionText: React.FC<{
  pot?: Pot;
  newPlan?: boolean;
  contributeFieldObj: FieldInputProps<contributeFieldType | undefined>;
}> = ({pot, newPlan, contributeFieldObj}) => {
  const {id, totalValue = ''} = pot || {};
  if (newPlan) {
    if (contributeFieldObj.value?.contributeTo !== NEW_PLAN_ID) {
      return null;
    }
  } else if (id !== contributeFieldObj.value?.contributeTo) {
    return null;
  }
  return (
    <Typography>
      <strong data-testid="post-contribution-id">{` - Post Initial Contribution: ${getCurrencyString(
        getCurrencyFloat(totalValue) +
          getCurrencyFloat(
            contributeFieldObj.value?.contributeFrom?.singleClientAmount || 0
          )
      )}`}</strong>
    </Typography>
  );
};

interface ContributeDestinationPlanFormProps {
  pot: Pot;
  destinationPots: Pot[];
  values: recommendFieldsType;
  contributeFieldObj: FieldInputProps<contributeFieldType | undefined>;
  contributeToMeta: FieldMetaProps<string | undefined>;
  recommendDetails: OVRecommendation | null;
  reviewId: string;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const ContributeDestinationPlanForm: React.FC<
  ContributeDestinationPlanFormProps
> = ({
  pot,
  destinationPots,
  values,
  contributeFieldObj,
  contributeToMeta,
  recommendDetails,
  reviewId,
  handleChange,
}) => {
  const disableChangingPlan = getDisableChangingPlan(recommendDetails);
  const showSubPlans =
    values.mainPlanType &&
    pot.children?.length &&
    hasWrapper(values.mainPlanType) &&
    getSavedChildSubPlans(destinationPots, pot.id).length &&
    includesSaltus(pot.productProvider?.name);

  return (
    <RecommendPlanForm
      id={pot.id}
      reccId={pot.originalOvrId ?? null}
      summary={
        <Box display="flex" flexDirection="row">
          {getSummary(pot)}
          &nbsp;
          <PostContributionText pot={pot} contributeFieldObj={contributeFieldObj} />
        </Box>
      }
      details={
        showSubPlans ? (
          false
        ) : (
          <ContributeNotSubplanDestinationAmounts
            disabled
            formikKeyPrefix="contribute.destinationValues"
            potInner={pot}
            mainPlanType={values.mainPlanType}
            comboFrequency={values.contribute?.comboFrequency}
          />
        )
      }
      curoId={pot.curoId}
      fixed={disableChangingPlan}
      subItems={
        showSubPlans ? (
          <ContributeExistingSubplans
            parentId={pot.id}
            pots={destinationPots}
            showEditing
            mainPlanType={values.mainPlanType}
            comboFrequency={values.contribute?.comboFrequency}
          />
        ) : (
          noSubplansMessage(values)
        )
      }
      validation={
        contributeFieldObj.value?.contributeTo === pot.id
          ? getValidation(contributeToMeta)
          : undefined
      }
      sameReview={pot.review?.id === reviewId}
      isSelected={contributeFieldObj.value?.contributeTo === pot.id}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};

interface ContributeNewPlanFormProps {
  contributeFieldObj: FieldInputProps<contributeFieldType | undefined>;
  contactName: string;
  setUpNewFieldMeta: FieldMetaProps<newPlanFields | undefined>;
  recommendDetails: OVRecommendation | null;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const ContributeNewPlanForm: React.FC<ContributeNewPlanFormProps> = ({
  contributeFieldObj,
  contactName,
  setUpNewFieldMeta,
  recommendDetails,
  handleChange,
}) => {
  const disableChangingPlan = getDisableChangingPlan(recommendDetails);

  return (
    <RecommendPlanForm
      id={NEW_PLAN_ID}
      reccId={null}
      newPlanPotId={contributeFieldObj.value?.newPlan?.existingPotId}
      fixed={disableChangingPlan}
      summary={
        <Box display="flex" flexDirection="row">
          <Typography>A New Plan</Typography>&nbsp;
          <PostContributionText newPlan contributeFieldObj={contributeFieldObj} />
        </Box>
      }
      isNewWrapperPlanForTransferOrContribution
      details={
        <Box flexDirection="column">
          <ShowIfDebugWrapper debugKey="forms">
            {contributeFieldObj.value?.newPlan?.existingPotId || 'Brand new plan'}
          </ShowIfDebugWrapper>
          <NewPlan
            context="contribute"
            formikKeyPrefix="contribute.newPlan"
            contactName={contactName}
          />
        </Box>
      }
      isSelected={contributeFieldObj.value?.contributeTo === NEW_PLAN_ID}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};
